/**
 * LOT-UI-03b.2 — Menu actions vente (Historique, DS Remobiles).
 * Dropdown porté sur document.body (position:fixed) : le tableau legacy
 * a overflow-x-auto, ce qui coupait le menu (Convertir en avoir invisible).
 */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  MoreVertical, DollarSign, FileText, Receipt, CheckCircle, Mail, Edit3, Trash2, Undo2, Eye,
} from 'lucide-react';

const menuBtnClass =
  'w-full flex items-center gap-3 px-3 py-3 min-h-[44px] text-sm text-left rounded-lg hover:bg-white/5 transition-colors';

const MENU_WIDTH = 260;

export default function HistoriqueVenteActionsMenu({
  vente,
  isAdmin,
  convertingRef,
  onPrintTicket,
  onViewTicketPdf,
  onCompleter,
  onFactureAcompte,
  onFactureDefinitive,
  onDefEmail,
  onConvertInvoice,
  onEmail,
  onChangePayment,
  onConvertAvoir,
  onDelete,
  testIdPrefix = 'historique',
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, maxHeight: 320 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const isRetrait = vente.type === 'retrait';
  const isAcompte = vente.type === 'acompte' && vente.device_ref;
  const isConverting = convertingRef === vente.ref_vente;
  const isPartial = vente.statut_paiement === 'partielle';

  const items = [];

  if (!isRetrait) {
    items.push({
      key: 'avoir',
      label: 'Convertir en avoir',
      icon: Undo2,
      onClick: () => { setOpen(false); onConvertAvoir?.(vente); },
      testId: `${testIdPrefix}-avoir-${vente.ref_vente}`,
      accent: 'var(--rm-success)',
    });
  }

  if (isPartial) {
    items.push({
      key: 'completer',
      label: `Compléter (${vente.reste_a_payer?.toFixed(2)} €)`,
      icon: DollarSign,
      onClick: () => { setOpen(false); onCompleter(vente); },
      testId: `${testIdPrefix}-completer-${vente.ref_vente}`,
      accent: 'var(--rm-red-light)',
    });
  }

  items.push({
    key: 'view-pdf',
    label: 'Voir le ticket (PDF)',
    icon: Eye,
    onClick: () => { setOpen(false); onViewTicketPdf?.(vente.ref_vente); },
    testId: `${testIdPrefix}-view-ticket-${vente.ref_vente}`,
  });

  items.push({
    key: 'ticket',
    label: 'Imprimer ticket 80mm',
    icon: FileText,
    onClick: () => { setOpen(false); onPrintTicket(vente.ref_vente); },
    testId: `${testIdPrefix}-ticket-${vente.ref_vente}`,
  });

  if (!isRetrait) {
    if (isAcompte) {
      items.push(
        {
          key: 'fac-acompte',
          label: "Facture d'acompte (PDF)",
          icon: Receipt,
          disabled: isConverting,
          onClick: () => { setOpen(false); onFactureAcompte(vente); },
          testId: `${testIdPrefix}-facture-acompte-${vente.ref_vente}`,
        },
        {
          key: 'fac-def',
          label: 'Facture définitive (PDF)',
          icon: CheckCircle,
          disabled: isConverting,
          onClick: () => { setOpen(false); onFactureDefinitive(vente); },
          testId: `${testIdPrefix}-facture-definitive-${vente.ref_vente}`,
        },
        {
          key: 'def-email',
          label: 'Email facture définitive',
          icon: Mail,
          onClick: () => { setOpen(false); onDefEmail(vente); },
          testId: `${testIdPrefix}-def-email-${vente.ref_vente}`,
        },
      );
    } else {
      items.push({
        key: 'facture',
        label: 'Convertir en facture PDF',
        icon: FileText,
        disabled: isConverting,
        onClick: () => { setOpen(false); onConvertInvoice(vente.ref_vente); },
        testId: `${testIdPrefix}-facture-${vente.ref_vente}`,
      });
    }
    items.push(
      {
        key: 'email',
        label: 'Envoyer facture par email',
        icon: Mail,
        onClick: () => { setOpen(false); onEmail(vente); },
        testId: `${testIdPrefix}-email-${vente.ref_vente}`,
      },
      {
        key: 'payment',
        label: 'Modifier mode de paiement',
        icon: Edit3,
        onClick: () => { setOpen(false); onChangePayment(vente); },
        testId: `${testIdPrefix}-payment-${vente.ref_vente}`,
      },
    );
  }

  if (isAdmin) {
    items.push({
      key: 'delete',
      label: 'Supprimer le ticket',
      icon: Trash2,
      onClick: () => { setOpen(false); onDelete(vente.ref_vente); },
      testId: `delete-vente-${vente.ref_vente}`,
      accent: 'var(--rm-red-light)',
    });
  }

  const placeMenu = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const estimated = items.length * 48 + 16;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUp = spaceBelow < estimated && spaceAbove > spaceBelow;
    const maxHeight = Math.max(240, Math.min(estimated, openUp ? spaceAbove : spaceBelow));
    let left = rect.right - MENU_WIDTH;
    left = Math.min(left, window.innerWidth - MENU_WIDTH - 8);
    left = Math.max(8, left);
    const top = openUp
      ? Math.max(8, rect.top - Math.min(estimated, maxHeight) - 4)
      : rect.bottom + 4;
    setCoords({ top, left, maxHeight });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    placeMenu();
    return undefined;
  }, [open, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const menu = open && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={menuRef}
        className="rounded-xl shadow-2xl py-1"
        style={{
          position: 'fixed',
          top: coords.top,
          left: coords.left,
          width: MENU_WIDTH,
          maxHeight: coords.maxHeight,
          overflowY: 'auto',
          zIndex: 10050,
          background: 'var(--rm-bg-panel)',
          border: '1px solid var(--rm-border-subtle)',
        }}
        data-testid={`${testIdPrefix}-actions-dropdown-${vente.ref_vente}`}
      >
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            disabled={item.disabled}
            onClick={item.onClick}
            className={`${menuBtnClass} disabled:opacity-50`}
            style={{ color: item.accent || 'var(--rm-gray-100)' }}
            data-testid={item.testId}
          >
            <item.icon className="w-4 h-4 flex-shrink-0" style={{ color: item.accent || 'var(--rm-gray-400)' }} />
            <span>{item.label}</span>
            {item.disabled && isConverting && (
              <div className="loading-spinner w-4 h-4 ml-auto" />
            )}
          </button>
        ))}
      </div>,
      document.body,
    )
    : null;

  return (
    <div className="inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="rm-touch rounded-xl border transition-colors"
        style={{
          borderColor: 'var(--rm-border-subtle)',
          background: 'var(--rm-bg-panel)',
          color: 'var(--rm-gray-300)',
        }}
        aria-label="Actions"
        aria-expanded={open}
        data-testid={`${testIdPrefix}-actions-menu-${vente.ref_vente}`}
      >
        <MoreVertical className="w-5 h-5" />
      </button>
      {menu}
    </div>
  );
}
